import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GitCompare, X, ChevronUp, ChevronDown } from "lucide-react";
import { compareStore, COMPARE_LIMIT } from "../lib/storage";
import { fmtMoney } from "../config";

/**
 * Sticky comparison tray. Appears at the bottom of the screen when at least
 * one vehicle has been added to compare. Shows up to 6 thumbnails with
 * remove buttons, and a "Compare Now" CTA that routes to /compare.
 *
 * Hidden on:
 *   - /admin/*, /dealer/*  (DealerShell already hides chrome)
 *   - /u/* customer upload portal
 *   - /compare itself (we're already there)
 */
export default function CompareTray() {
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const sync = () => setItems(compareStore.list());
    sync();
    window.addEventListener("ssc-storage", sync);
    return () => window.removeEventListener("ssc-storage", sync);
  }, []);

  const hidden =
    loc.pathname.startsWith("/admin") ||
    loc.pathname.startsWith("/dealer") ||
    loc.pathname.startsWith("/u/") ||
    loc.pathname === "/compare";

  if (hidden || items.length === 0) return null;

  const titleOf = (v) => `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim();

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[70] bg-[var(--ssc-navy)] text-white shadow-[0_-8px_30px_rgba(0,0,0,0.25)] border-t-2 border-[var(--ssc-blue)]"
      data-testid="compare-tray"
    >
      <div className="max-w-[1440px] mx-auto px-3 md:px-6 py-2 md:py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <GitCompare size={16} className="text-[var(--ssc-blue)] flex-shrink-0" />
            <span className="text-sm md:text-base font-extrabold">
              Compare{" "}
              <span className="text-[var(--ssc-blue)] tabular-nums">{items.length}</span>
              <span className="text-white/60 font-normal"> / {COMPARE_LIMIT}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => compareStore.clear()}
              className="text-[11px] md:text-xs font-bold text-white/70 hover:text-white underline-offset-2 hover:underline"
              data-testid="compare-tray-clear"
            >
              Clear all
            </button>
            <Link
              to="/compare"
              className="ssc-btn ssc-btn-primary !py-1.5 !px-3 !text-xs md:!text-sm"
              data-testid="compare-tray-compare-now"
            >
              Compare Now
            </Link>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="text-white/70 hover:text-white p-1 md:hidden"
              aria-label={collapsed ? "Expand compare tray" : "Collapse compare tray"}
              data-testid="compare-tray-toggle"
            >
              {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="flex gap-2 overflow-x-auto pb-1 snap-x">
            {items.map((v) => (
              <div
                key={v.slug || v.id}
                className="relative flex-shrink-0 w-[150px] md:w-[180px] bg-white/5 border border-white/10 rounded-lg overflow-hidden snap-start"
                data-testid={`compare-tray-item-${v.stock_number}`}
              >
                <button
                  type="button"
                  onClick={() => compareStore.remove(v.slug)}
                  className="absolute top-1 right-1 z-10 w-6 h-6 rounded-full bg-black/60 hover:bg-red-500 text-white grid place-items-center transition"
                  aria-label="Remove from compare"
                  data-testid={`compare-tray-remove-${v.stock_number}`}
                >
                  <X size={12} />
                </button>
                {v.photos?.[0]?.url ? (
                  <img
                    src={v.photos[0].url}
                    alt={titleOf(v)}
                    className="w-full h-[60px] md:h-[72px] object-cover"
                  />
                ) : (
                  <div className="w-full h-[60px] md:h-[72px] bg-white/5" />
                )}
                <div className="p-1.5">
                  <div className="text-[10px] md:text-[11px] font-bold leading-tight line-clamp-2">
                    {titleOf(v)}
                  </div>
                  <div className="text-[10px] md:text-[11px] text-[var(--ssc-blue)] font-extrabold tabular-nums mt-0.5">
                    {fmtMoney(v.price)}
                  </div>
                </div>
              </div>
            ))}
            {items.length < COMPARE_LIMIT && (
              <div className="flex-shrink-0 w-[150px] md:w-[180px] h-[100px] md:h-[112px] border-2 border-dashed border-white/20 rounded-lg grid place-items-center text-white/40 text-[11px] text-center px-2">
                Add up to {COMPARE_LIMIT - items.length} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
